import { TabData } from '@ddt-web/data-service';
import { NumberUtil, ObjectUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { ChartFactory } from '../factories';
import { IChartData } from '../models';
import { DefaultChartConfig } from './default-chart.config';
import { ColumnChartName } from './enums';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

/**
 * EMPTY_CHART_COLUMN_CONFIG: configuration settings unique to a empty Column Chart (simply the column and nothing else).
 */
const EMPTY_CHART_COLUMN_CONFIG: any = {
	plotOptions: {
		column: {
			stacking: 'normal',
			dataLabels: {
				enabled: false
			},
			pointWidth: 50
		}
	},
	tooltip: {
		formatter: function (): string {
			return this.point.series.name + ' ' + NumberUtil.numberFormatter(this.point.y, '%');
		},
		backgroundColor: '#f9f9f9',
		borderColor: '#a5a5a5',
		borderRadius: 0,
		shadow: false,
		useHTML: true,
		style: {
			fontFamily: '"FortBook", Arial, sans-serif'
		}
	},
	xAxis: {
		lineWidth: 0,
		minorGridLineWidth: 0,
		gridLineWidth: 0,
		labels: {
			enabled: false
		},
		minorTickLength: 0,
		tickLength: 0,
		title: { text: null }
	},
	yAxis: {
		lineWidth: 0,
		minorGridLineWidth: 0,
		gridLineWidth: 0,
		labels: {
			enabled: false
		},
		minorTickLength: 0,
		tickLength: 0,
		title: { text: null }
	}
};

export class EmptyChartColumnConfig extends DefaultChartConfig {
	private chartData: IChartData;

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: EmptyColumnChartCustomAttributes
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, EMPTY_CHART_COLUMN_CONFIG);
		this.configuration.chart.type = ChartType.Column;
		if (this.data instanceof TabData) {
			this.chartData = ChartFactory.getStackedData(this.data, this.dataIndex);
		}
	}

	public render(): void {
		this.updateDefaultConfiguration();
		if (!this.name) {
			this.renderDefaultChart();
		} else if (this.name === ColumnChartName.Exposure) {
			this.renderExposureChart();
		}
	}

	private updateDefaultConfiguration(): void {
		let marginTop: number = 0;
		let spacingBottom: number = 0;
		const sibling: any = this.container.parentElement.previousElementSibling || this.container.parentElement.nextElementSibling;
		if (sibling && sibling.tagName.toLowerCase() === 'dl-display-table') {
			marginTop = sibling.querySelector('header').offsetHeight + 1;
			spacingBottom = 10;
		}
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: marginTop,
				spacingBottom: spacingBottom
			},
			colors: this.chartData.colors,
			series: this.chartData.series,
			xAxis: {
				categories: this.chartData.scenarios
			},
			yAxis: {
				tickPositions: get(this.customAttributes, 'tickPositions', null) ? this.customAttributes.tickPositions : null
			}
		});
	}

	private updateDefaultConfigurationForPDF(): void {
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 0
			},
			plotOptions: {
				column: {
					pointWidth: 30
				}
			},
			colors: this.chartData.colors,
			series: this.chartData.series,
			xAxis: {
				visible: false
			},
			yAxis: {
				visible: false,
				tickPositions: get(this.customAttributes, 'tickPositions', null) ? this.customAttributes.tickPositions : null
			}
		});
	}

	private renderDefaultChart(): void {
		Highcharts.chart(this.configuration);
	}

	private renderExposureChart(): void {
		Highcharts.chart(
			ObjectUtil.extend(this.configuration, {
				colors: ChartFactory.getExposureColorsForTabData(this.data),
				plotOptions: {
					column: {
						stacking: 'percent',
						dataLabels: {
							enabled: true,
							formatter: function (): any {
								return this.y > 0 ? this.series.name : null;
							},
							style: {
								textOutline: 'none',
								fontSize: '12px'
							}
						}
					}
				},
				series: ChartFactory.getExposureColumnSeriesForTabData(this.data)
			})
		);
	}

	public getConfigurationForPDF(): any {
		this.chartData = ChartFactory.getStackedData(this.data, this.dataIndex);
		this.updateDefaultConfigurationForPDF();
		return this.configuration;
	}
}

export interface EmptyColumnChartCustomAttributes {
	tickPositions: any[];
}
